/**
 * 市场矩阵热力图组件
 * 展示9宫格市场分类分布
 */

import React from 'react';
import './MarketMatrixHeatmap.css';

interface MatrixData {
  matrix: number[][];
  labels: string[][];
  x_labels: string[];
  y_labels: string[];
}

interface Props {
  data: MatrixData;
  className?: string;
}

const MarketMatrixHeatmap: React.FC<Props> = ({ data, className = '' }) => {
  if (!data || !data.matrix) {
    return <div className="matrix-heatmap-empty">暂无数据</div>;
  }

  const { matrix, labels, x_labels, y_labels } = data;
  const maxValue = Math.max(...matrix.flat());

  const getCellColor = (value: number): string => {
    if (maxValue === 0) return '#f8f9fa';

    // 使用平方根进行非线性映射，使低值区分更明显
    const intensity = Math.sqrt(value / maxValue);

    // 优化的轻盈淡雅配色 - 微交互风格
    // 浅色: #FFFFFF (255, 255, 255) - 纯白融入背景
    // 中色: #E1F0FF (225, 240, 255) - 极淡蓝
    // 深色: #9AC5F4 (154, 197, 244) - 柔和粉蓝

    let r, g, b;
    if (intensity < 0.5) {
      // 浅色到中色的过渡
      const t = intensity * 2; // 0 到 1
      r = Math.floor(255 + (225 - 255) * t);
      g = Math.floor(255 + (240 - 255) * t);
      b = Math.floor(255 + (255 - 255) * t);
    } else {
      // 中色到深色的过渡
      const t = (intensity - 0.5) * 2; // 0 到 1
      r = Math.floor(225 + (154 - 225) * t);
      g = Math.floor(240 + (197 - 240) * t);
      b = Math.floor(255 + (244 - 255) * t);
    }

    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div className={`market-matrix-heatmap ${className}`}>
      <div className="matrix-container">
        {/* Y轴标签 */}
        <div className="y-axis-labels">
          {y_labels.map((label, idx) => (
            <div key={idx} className="y-label">
              {label}
            </div>
          ))}
        </div>

        {/* 矩阵主体 */}
        <div className="matrix-grid">
          {matrix.map((row, rowIdx) => (
            <div key={rowIdx} className="matrix-row">
              {row.map((value, colIdx) => (
                <div
                  key={`${rowIdx}-${colIdx}`}
                  className="matrix-cell"
                  style={{ backgroundColor: getCellColor(value) }}
                >
                  <div className="cell-label">{labels[rowIdx][colIdx]}</div>
                  <div className="cell-value">{value}</div>
                </div>
              ))}
            </div>
          ))}

          {/* X轴标签 */}
          <div className="x-axis-labels">
            {x_labels.map((label, idx) => (
              <div key={idx} className="x-label">
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 图例 */}
      <div className="matrix-legend">
        <div className="legend-title">关键词数量</div>
        <div className="legend-gradient">
          <span>0</span>
          <div className="gradient-bar"></div>
          <span>{maxValue}</span>
        </div>
      </div>
    </div>
  );
};

export default MarketMatrixHeatmap;
